/**
 * ============================================================================
 * TENANT CONTEXT - WHITE-LABEL MULTI-TENANCY
 * ============================================================================
 * 
 * Detects and provides tenant configuration based on the current domain.
 * 
 * KEY PROPERTY: `isMasterTenant`
 *    - TRUE  → MEDD master tenant (sim.medd.com.au) → Uses WordPress SSO
 *    - FALSE → White-label tenant → Uses DIRECT Supabase email/password login
 * 
 * TENANT DETECTION:
 *    Domain: gsk.medd.com.au → Subdomain: "gsk" → Tenant lookup → GSK tenant
 *    Domain: sim.medd.com.au → Master tenant (ID: 00000000-0000-0000-0000-000000000001)
 * 
 * WHITE-LABEL TENANTS (use direct login, NOT WordPress SSO):
 *    - GSK (gsk.medd.com.au)
 *    - Boston Scientific (boston.medd-sim.com)
 *    - Edwards Lifesciences (edwards.medd-sim.com)
 *    - Any tenant that is NOT the master tenant
 * 
 * ⚠️  CRITICAL: Only isMasterTenant=true should use WordPress SSO via medd.com.au
 * ⚠️  All other tenants authenticate directly with Supabase email/password
 * 
 * See: docs/AUTHENTICATION.md for full documentation
 * ============================================================================
 */

import { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { supabase, setActiveTenantId } from '@/integrations/supabase/client';

// Default branding (MEDD-SIM core)
const DEFAULT_BRANDING = {
  tenant_id: '00000000-0000-0000-0000-000000000001',
  tenant_name: 'MEDD-SIM',
  tenant_slug: 'medd-sim',
  logo_url: '/medd-sim-logo.svg',
  logo_dark_url: null,
  favicon_url: '/favicon.ico',
  primary_color: '#233C6F',
  secondary_color: '#EF5E33',
  accent_color: '#10B981',
  background_color: '#FFFFFF',
  text_color: '#1F2937',
  custom_css: null,
  font_family: 'Inter',
  email_from_name: 'MEDD-SIM',
  email_from_address: 'noreply@medd.com.au',
  powered_by_visible: false,
  powered_by_text: 'Powered by MEDD-SIM',
  powered_by_url: 'https://medd.com.au',
  terms_url: 'https://medd.com.au/terms',
  privacy_url: 'https://medd.com.au/privacy',
  support_email: 'support@medd.com.au',
  login_title: 'Welcome to MEDD-SIM',
  login_subtitle: 'AI-Powered Medical Simulation Training',
  login_background_url: null,
  features: {} as Record<string, boolean | string | number | null>,
};

export type TenantSettings = typeof DEFAULT_BRANDING;

interface TenantContextType {
  tenant: TenantSettings;
  loading: boolean;
  error: string | null;
  isMasterTenant: boolean;
  refreshTenant: () => Promise<void>;
  cssVariables: Record<string, string>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

// Extract subdomain from hostname
function getSubdomainFromHost(hostname: string): string | null {
  // Handle localhost
  if (hostname === 'localhost' || hostname.startsWith('localhost:')) {
    return null;
  }
  
  // Handle IP addresses
  if (/^\d+\.\d+\.\d+\.\d+/.test(hostname)) {
    return null;
  }
  
  const parts = hostname.split('.');
  
  // e.g., connectthedocs.medd.com.au → connectthedocs
  // e.g., app.medd.com.au → app (master)
  // e.g., medd.com.au → null
  if (parts.length >= 3) {
    const subdomain = parts[0];
    // Skip common subdomains that aren't tenant identifiers
    if (['www', 'app', 'api', 'admin'].includes(subdomain)) {
      return null;
    }
    return subdomain;
  }
  
  return null;
}

// Detect tenant from current URL
async function detectTenant(): Promise<TenantSettings | null> {
  const hostname = window.location.hostname;
  const subdomain = getSubdomainFromHost(hostname);
  
  
  // 🧪 STAGING DOMAIN MAPPING
  // Map staging domains to their production equivalents for tenant lookup
  let lookupDomain = hostname;
  let lookupSubdomain = subdomain;
  
  // staging-sim.medd.com.au → maps to master tenant (sim.medd.com.au)
  if (hostname === 'staging-sim.medd.com.au' || hostname.startsWith('staging-sim.')) {
    lookupDomain = 'sim.medd.com.au';
    lookupSubdomain = null; // Master tenant has no subdomain
  }
  // staging-demo.medd.com.au → maps to staging-demo tenant
  else if (hostname === 'staging-demo.medd.com.au' || hostname.startsWith('staging-demo.')) {
    lookupSubdomain = 'staging-demo';
  }
  // Ordinary Vercel previews default to master. Dedicated enterprise builds
  // set VITE_SSO_DOMAIN and must resolve their own tenant by the exact Vercel
  // hostname (for example, the isolated Boston staging deployment).
  else if (hostname.includes('vercel.app') && !import.meta.env.VITE_SSO_DOMAIN) {
    // For Vercel previews, default to master tenant for full testing access
    lookupDomain = 'sim.medd.com.au';
    lookupSubdomain = null;
  }
  
  try {
    // Try to fetch tenant by domain or subdomain
    const { data, error } = await supabase.rpc('get_tenant_by_domain', {
      p_domain: lookupDomain
    });
    
    if (error) {
      console.error('Tenant lookup error:', error);
      return null;
    }
    
    if (data && data.length > 0) {
      const tenant = data[0];
      
      // Fetch full tenant settings
      const { data: settings, error: settingsError } = await supabase.rpc('get_tenant_settings', {
        p_tenant_id: tenant.tenant_id
      });
      
      if (settingsError) {
        console.error('Tenant settings error:', settingsError);
        return null;
      }
      
      if (settings && settings.length > 0) {
        return {
          ...DEFAULT_BRANDING,
          ...settings[0],
          features: settings[0].features || {},
        };
      }
    }
    
    // Check if subdomain matches a tenant slug
    if (lookupSubdomain) {
      const { data: slugData, error: slugError } = await supabase
        .from('tenants')
        .select('id')
        .eq('slug', lookupSubdomain)
        .eq('is_active', true)
        .single();
      
      if (!slugError && slugData) {
        const { data: settings } = await supabase.rpc('get_tenant_settings', {
          p_tenant_id: slugData.id
        });
        
        if (settings && settings.length > 0) {
          return {
            ...DEFAULT_BRANDING,
            ...settings[0],
            features: settings[0].features || {},
          };
        }
      }
    }
    
    return null;
    
  } catch (err) {
    console.error('Tenant detection failed:', err);
    return null;
  }
}

// Convert hex to HSL for Tailwind CSS variables
function hexToHsl(hex: string): string {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Parse hex to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  
  // Return HSL values for Tailwind (without hsl() wrapper)
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

// Apply CSS variables to document root
function applyCssVariables(tenant: TenantSettings): Record<string, string> {
  const vars: Record<string, string> = {
    '--tenant-primary': tenant.primary_color,
    '--tenant-secondary': tenant.secondary_color,
    '--tenant-accent': tenant.accent_color,
    '--tenant-background': tenant.background_color,
    '--tenant-text': tenant.text_color,
    '--tenant-font': tenant.font_family,
  };
  
  // Apply to document root
  const root = document.documentElement;
  Object.entries(vars).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
  
  // 🎨 CRITICAL: Override the main --primary color for full UI theming
  // This makes buttons, sidebar, links, etc. use the tenant's color
  if (tenant.primary_color) {
    const primaryHsl = hexToHsl(tenant.primary_color);
    root.style.setProperty('--primary', primaryHsl);
    
    // Also set sidebar color for the navigation
    root.style.setProperty('--sidebar-primary', primaryHsl);
    root.style.setProperty('--sidebar-primary-foreground', '0 0% 100%'); // White text
    
    // Set accent color if available
    if (tenant.accent_color) {
      const accentHsl = hexToHsl(tenant.accent_color);
      root.style.setProperty('--accent', accentHsl);
    }
  }
  
  // Apply custom CSS if provided
  if (tenant.custom_css) {
    let styleEl = document.getElementById('tenant-custom-css');
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'tenant-custom-css';
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = tenant.custom_css;
  }
  
  // Update favicon
  if (tenant.favicon_url) {
    const faviconEl = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
    if (faviconEl) {
      faviconEl.href = tenant.favicon_url;
    }
  }
  
  // Update page title.
  // Dedicated enterprise deployments (VITE_SSO_DOMAIN set) own their title via
  // the boot-time swap in main.tsx — don't override it here, or the tenant
  // name would clobber the enterprise wording after load (the flicker Codex
  // flagged: "Simulation Training" → "Medical Simulation").
  const isEnterpriseDeployment = Boolean((import.meta as any).env?.VITE_SSO_DOMAIN);
  if (!isEnterpriseDeployment && tenant.tenant_name && tenant.tenant_name !== 'MEDD-SIM') {
    document.title = `${tenant.tenant_name} | Medical Simulation`;
  }
  
  return vars;
}

interface TenantProviderProps {
  children: ReactNode;
}

export function TenantProvider({ children }: TenantProviderProps) {
  const [tenant, setTenant] = useState<TenantSettings>(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const isMasterTenant = useMemo(() => {
    return tenant.tenant_id === '00000000-0000-0000-0000-000000000001';
  }, [tenant.tenant_id]);
  
  const cssVariables = useMemo(() => {
    return applyCssVariables(tenant);
  }, [tenant]);
  
  const refreshTenant = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const detected = await detectTenant();
      if (detected) {
        setTenant(detected);
        // Wire tenant to Supabase client — all subsequent queries include x-tenant-id header
        // so RLS functions (get_current_tenant_id) scope to the correct tenant
        setActiveTenantId(detected.tenant_id);
      } else {
        setTenant(DEFAULT_BRANDING);
        setActiveTenantId(DEFAULT_BRANDING.tenant_id);
      }
    } catch (err) {
      console.error('Failed to refresh tenant:', err);
      setError(err instanceof Error ? err.message : 'Failed to load tenant');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    refreshTenant();
  }, []);
  
  const value: TenantContextType = {
    tenant,
    loading,
    error,
    isMasterTenant,
    refreshTenant,
    cssVariables,
  };
  
  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}

// Hook to check feature flags
export function useTenantFeature(featureName: string, defaultValue = false): boolean {
  const { tenant } = useTenant();
  const value = tenant.features?.[featureName];
  return typeof value === 'boolean' ? value : defaultValue;
}

export function useCpdEnabled(): boolean {
  const { tenant, loading } = useTenant();
  if (loading) return false;
  const value = tenant.features?.cpd_enabled;
  return typeof value === 'boolean' ? value : true;
}

export function useExpandedAnamAvatarCatalogueEnabled(): boolean {
  const { tenant, loading } = useTenant();
  return loading
    ? false
    : tenant.features?.expanded_anam_avatar_catalogue === true;
}

export function useEnterpriseHierarchyEnabled(): boolean {
  const { tenant, loading } = useTenant();
  return loading
    ? false
    : tenant.features?.enterprise_hierarchy_enabled === true;
}

// Component for tenant-aware logo
export function TenantLogo({ 
  className = '', 
  darkMode = false 
}: { 
  className?: string; 
  darkMode?: boolean; 
}) {
  const { tenant } = useTenant();
  const logoUrl = darkMode && tenant.logo_dark_url 
    ? tenant.logo_dark_url 
    : tenant.logo_url;
    
  if (!logoUrl) {
    return <span className={`font-bold text-xl ${className}`}>{tenant.tenant_name}</span>;
  }
  
  return (
    <img 
      src={logoUrl} 
      alt={`${tenant.tenant_name} logo`} 
      className={className}
    />
  );
}

// Component for "Powered by" footer
export function PoweredBy({ className = '' }: { className?: string }) {
  const { tenant } = useTenant();
  
  if (!tenant.powered_by_visible) {
    return null;
  }
  
  return (
    <div className={`text-sm text-muted-foreground ${className}`}>
      <a 
        href={tenant.powered_by_url || 'https://medd.com.au'} 
        target="_blank" 
        rel="noopener noreferrer"
        className="hover:underline"
      >
        {tenant.powered_by_text || 'Powered by MEDD-SIM'}
      </a>
    </div>
  );
}

// Hook to get themed colors for charts/components
export function useTenantColors() {
  const { tenant } = useTenant();
  
  return useMemo(() => ({
    primary: tenant.primary_color,
    secondary: tenant.secondary_color,
    accent: tenant.accent_color,
    background: tenant.background_color,
    text: tenant.text_color,
    // Generate a palette for charts
    chartColors: [
      tenant.primary_color,
      tenant.secondary_color,
      tenant.accent_color,
      '#6366F1', // Indigo
      '#F59E0B', // Amber
      '#EC4899', // Pink
    ],
  }), [tenant]);
}
